import { Table } from '@tanstack/react-table';
import { useCallback } from 'react';

export function useTableExport<TData>(
  table: Table<TData>,
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void,
  onPdf?: (columnIds: string[], rows: Partial<TData>[]) => void,
) {
  const getExportColumns = useCallback(() => {
    const columnOrder = table.getState().columnOrder;
    return table
      .getVisibleFlatColumns()
      .filter((el) => el.id !== 'actions' && el.id !== 'select')
      .filter((column) => columnOrder.includes(column.id))
      .sort((a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id))
      .map((column) => column.id);
  }, [table]);

  const getExportData = useCallback(() => {
    return table.getRowModel().rows.map((el) =>
      el
        .getVisibleCells()
        .filter((c) => c.column.id !== 'actions' && c.column.id !== 'select')
        .reduce(
          (acc, el) => {
            acc[el.column.id] = el.getValue();
            return acc;
          },
          {} as Record<string, unknown>,
        ),
    );
  }, [table]);

  const handleExportExcel = useCallback(() => {
    if (!onExcel) return;
    const columns = getExportColumns();
    const rows = getExportData();

    onExcel(columns, rows as Partial<TData>[]);
  }, [onExcel, getExportColumns]);

  const handleExportPdf = useCallback(() => {
    if (!onPdf) return;
    const columns = getExportColumns();
    const rows = getExportData();

    onPdf(columns, rows as Partial<TData>[]);
  }, [onPdf, getExportColumns]);

  return {
    handleExportExcel,
    handleExportPdf,
  };
}
